import React from 'react';
import { Card } from 'react-bootstrap';
import { StatCard, StatCardSkeleton } from '../solar/StatCard';
import JahresDiagramm from './JahresDiagramm';
import SollVsIstKachel from './SollVsIstKachel';

/** TrendStatus-Enum-Werte (entspricht Backend) */
const TREND_STATUS = {
    0: { label: 'Unzureichende Daten', color: '#6c757d', icon: 'bi-question-circle' },
    1: { label: 'Stabil', color: '#198754', icon: 'bi-check-circle' },
    2: { label: 'Leichter Ruckgang', color: '#ffc107', icon: 'bi-dash-circle' },
    3: { label: 'Signifikanter Ruckgang', color: '#fd7e14', icon: 'bi-exclamation-triangle' },
    4: { label: 'Kritischer Ruckgang', color: '#dc3545', icon: 'bi-x-octagon' },
    5: { label: 'Datenqualitatsproblem', color: '#6f42c1', icon: 'bi-exclamation-circle' },
};

/** AntwortStatus-Enum-Werte (entspricht Backend) */
const ANTWORT_STATUS = {
    0: 'OK',
    1: 'KEINE_DATEN',
    2: 'ZU_WENIGE_JAHRE',
    3: 'TEILWEISE_VERFUEGBAR',
    4: 'BERECHNUNG_FEHLGESCHLAGEN',
};

/** DataQualityStatus-Enum-Werte (entspricht Backend) */
const QUALITAT_LABEL = {
    0: 'Gut',
    1: 'Eingeschrankt',
    2: 'Mangelhaft',
};

/** YearCompletenessStatus-Enum-Werte (entspricht Backend) */
const VOLLSTAENDIGKEIT_LABEL = {
    0: 'Vollstandig',
    1: 'Unvollstandig',
    2: 'Aktuell (unvollstandig)',
};

/**
 * Haupt-Dashboard-Komponente fur PV-Jahresvergleich.
 * Zeigt KPIs, Trend-Info, Warnhinweise, Diagramm und Tabelle.
 *
 * @param {object} props
 * @param {object}    props.data — PvYearComparisonResponse von der API
 * @param {function}  props.onRefresh — Callback zum Neuladen
 * @param {object}    [props.emptyState] – DTO-Teil bei „zu wenige Jahre" (optional)
 * @param {object}    [props.sollVsIstData] — Soll-vs-Ist Daten (api: jahr + monatsertragVergleich[])
 * @param {Array}     [props.verfuegbareJahre] — alle verfuegbaren Jahre
 * @param {function}  [props.onLoadSollVsIst] — Callback um Soll-vs-Ist fuer anderes Jahr zu laden
 */
export default function PvDashboard({ data, onRefresh, emptyState, sollVsIstData, verfuegbareJahre, onLoadSollVsIst }) {
    const trendInfo = TREND_STATUS[data.trend && data.trend.status] || TREND_STATUS[0];
    const statusLabel = ANTWORT_STATUS[data.status];

    return (
        <div className="pv-dashboard" role="main" aria-label="PV-Jahresvergleich Dashboard">
            {/* ——— Kopfbereich ——— */}
            <div className="dashboard-header mb-4">
                <h3>
                    <i className="bi bi-graph-up-arrow me-2" aria-hidden="true" />
                    PV-Jahresvergleich
                </h3>
                <p className="text-muted mb-1">
                    Anlage {data.anlageId} · {data.kwp} kWp ·
                    Zeitraum {data.startjahr}—{data.endjahr}
                </p>

                {/* Status-Badge */}
                {statusLabel !== 'OK' && (
                    <span className="badge bg-warning text-dark mt-1" role="status">
                        Status: {statusLabel}
                    </span>
                )}
            </div>

            {/* ——— Leerzustand: Zu wenige Jahre f&uuml;r Trendanalyse ——— */}
            {emptyState && (
                <div className="alert alert-info mb-3" role="alert">
                    <i className="bi bi-info-circle me-1" aria-hidden="true" />
                    {emptyState.fehlermeldung
                        || 'Nur begrenzte Daten verf&uuml;gbar — Trendanalyse nicht durchf&uuml;hrbar.'}
                </div>
            )}

            {/* ——— Fehlermeldung ——— */}
            {data.fehlermeldung && (
                <div className="alert alert-info mb-3" role="alert">
                    <i className="bi bi-info-circle me-1" />
                    {data.fehlermeldung}
                </div>
            )}

            {/* ——— Warnhinweise ——— */}
            {data.warnhinweise && data.warnhinweise.length > 0 && (
                <div className="mb-3">
                    {data.warnhinweise.map((msg, i) => (
                        <div key={i} className="alert alert-warning py-2 mb-1" role="alert">
                            <i className="bi bi-exclamation-triangle me-1" />
                            {msg}
                        </div>
                    ))}
                </div>
            )}

            {/* ——— KPI-Reihe ——— */}
            <KpiSection data={data} trendInfo={trendInfo} />

            {/* ——— Trend-Detail ——— */}
            {data.trend && (
                <Card className="mb-3 trend-card">
                    <Card.Body>
                        <h6 className="card-title">
                            <i className="bi bi-graph-down-arrow me-1" />
                            Trendanalyse
                        </h6>
                        <TrendDetail data={data.trend} trendInfo={trendInfo} />
                    </Card.Body>
                </Card>
            )}

            {/* ——— Ausgeschlossene Jahre ——— */}
            {data.ausgeschlosseneJahre && data.ausgeschlosseneJahre.length > 0 && (
                <Card className="mb-3">
                    <Card.Body>
                        <h6 className="card-title">
                            <i className="bi bi-calendar-x me-1" />
                            Ausgeschlossene Jahre
                        </h6>
                        <ExcludedYearsList items={data.ausgeschlosseneJahre} />
                    </Card.Body>
                </Card>
            )}

            {/* ——— Jahreswerte-Ubersicht (kompakt) ——— */}
            {data.jahreswerte && data.jahreswerte.length > 0 && (
                <Card className="mb-3">
                    <Card.Body>
                        <h6 className="card-title">
                            <i className="bi bi-table me-1" />
                            Jahreswerte-Ubersicht
                        </h6>
                        <YearOverviewTable items={data.jahreswerte} />
                    </Card.Body>
                </Card>
            )}

            {/* ——— Jahresdiagramm mit Trendlinie (Schritt 20) ——— */}
            {data.jahreswerte && data.jahreswerte.length > 0 && (
                <JahresDiagramm jahreswerte={data.jahreswerte} />
            )}

            {/* ——— Soll-vs-Ist Monatsvergleich ——— */}
            {sollVsIstData && sollVsIstData.monatsertragVergleich && (
                <SollVsIstKachel
                    daten={sollVsIstData.monatsertragVergleich}
                    jahr={sollVsIstData.jahr}
                    verfuegbareJahre={verfuegbareJahre || []}
                    onLoadYear={onLoadSollVsIst}
                />
            )}

            {/* ——— Footer mit Meta-Info ——— */}
            <footer className="dashboard-footer text-muted mt-4">
                <small>
                    Berechnet am{' '}
                    {formatDateTime(data.berechnungszeitpunkt)} ·
                    Version {data.berechnungsversion}
                    {data.verwendeteSchwellenwerte && (
                        <>
                            {' '}· {data.verwendeteSchwellenwerte}
                        </>
                    )}
                </small>
            </footer>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  KPI-Reihe                                                         */
/* ------------------------------------------------------------------ */

function KpiSection({ data, trendInfo }) {
    var ytd = data.ytdVergleich;

    // KPI-Item: YTD-Ertrag
    var ytdValue = ytd ? formatNumber(ytd.aktuellerErtragKWh) + ' kWh' : '—';

    // KPI-Item: Abweichung zum Vorjahr
    var ytdChange = ytd && ytd.abweichungZumVorjahrProzent !== undefined
        ? (ytd.abweichungZumVorjahrProzent >= 0 ? '+' : '')
            + formatNumber(ytd.abweichungZumVorjahrProzent, 1) + '%'
        : '—';

    // KPI-Item: Trendanderung
    var trendChange = data.trend
        ? (data.trend.aenderungRelativProzentProJahr >= 0 ? '+' : '')
            + formatNumber(data.trend.aenderungRelativProzentProJahr, 2) + '%/Jahr'
        : '—';

    // KPI-Item: Datenqualitaet
    var qualitaet = data.gesamteDatenqualitaetProzent !== undefined
        ? formatNumber(data.gesamteDatenqualitaetProzent, 0) + '%'
        : '—';

    return (
        <div className="row mb-3 g-2">
            <div className="col-6 col-md-3">
                <StatCard
                    label="YTD-Ertrag"
                    value={ytdValue}
                    icon={<i className="bi bi-sun" />}
                    variant="energy"
                    subLabel={buildSublabelYtd(data)}
                />
            </div>
            <div className="col-6 col-md-3">
                <StatCard
                    label="Vorjahresabweichung"
                    value={ytdChange}
                    icon={<i className="bi bi-arrow-repeat" />}
                    variant="progress"
                    subLabel={buildSublabelYtdAbw(data)}
                />
            </div>
            <div className="col-6 col-md-3">
                <StatCard
                    label={trendInfo.label}
                    value={trendChange}
                    icon={<i className={trendInfo.icon} />}
                    variant="specific"
                    subLabel={buildSublabelTrend(data)}
                />
            </div>
            <div className="col-6 col-md-3">
                <StatCard
                    label="Datenqualitaet"
                    value={qualitaet}
                    icon={<i className="bi bi-patch-check" />}
                    variant="energy"
                    subLabel={buildSublabelQualitaet(data)}
                />
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Modal-Erklaeungen                                                   */
/* ------------------------------------------------------------------ */

function buildSublabelYtd(data) {
    var ytd = data.ytdVergleich;
    if (!ytd) return 'Keine YTD-Daten verfuegbar.';
    return [
        'Ertrag vom 1. Januar bis heute',
        '(' + ytd.tageVorhanden + ' von ' + ytd.tageSoll + ' Tagen)',
        '',
        'Letztes Jahr im selben Zeitraum:',
        formatNumber(ytd.vorjahresErtragKWh, 0) + ' kWh',
    ].join('\n');
}

function buildSublabelYtdAbw(data) {
    var ytd = data.ytdVergleich;
    if (!ytd || ytd.abweichungZumVorjahrProzent === undefined) return 'Kein Vergleich moeglich.';
    var abw = ytd.abweichungZumVorjahrProzent;
    var richtung = abw >= 0 ? 'mehr' : 'weniger';
    return [
        'Vergleich zum Vorjahr:',
        '',
        'Im gleichen Zeitraum war der Ertrag',
        'um ' + formatNumber(Math.abs(abw), 1) + '% ' + richtung + '.',
    ].join('\n');
}

function buildSublabelTrend(data) {
    if (!data.trend) return 'Keine Trenddaten verfuegbar.';
    var t = data.trend;
    return [
        'Jaehrliche Aenderung:',
        formatNumber(t.aenderungAbsolutKWhProJahr, 0) + ' kWh/Jahr (absolut)',
        formatNumber(t.aenderungRelativProzentProJahr, 2) + '%/Jahr (relativ)',
        '',
        t.anzahlVerwendeteJahre + ' vollstaendige Jahre',
    ].join('\n');
}

function buildSublabelQualitaet(data) {
    var pct = data.gesamteDatenqualitaetProzent;
    if (pct === undefined) return 'Keine Qualitaetsangabe.';
    var auskunft;
    if (pct >= 95) auskunft = 'Sehr gut — Datenbasis umfassend und zuverlässig.';
    else if (pct >= 80) auskunft = 'Gut — geringfuegige Luecken koennen auftreten.';
    else auskunft = 'Eingeschraenkt — fehlende Tage beeinflussen die Auswertung.';
    return [
        'Durchschnittliche Vollstaendigkeit:',
        formatNumber(pct, 1) + '% ueber alle Jahre',
        '',
        auskunft,
    ].join('\n');
}

/* ------------------------------------------------------------------ */
/*  Trend-Detail-Karte                                                */
/* ------------------------------------------------------------------ */

function TrendDetail({ data, trendInfo }) {
    var bewertung = computeAnlagenebewertung(
        data.aenderungRelativProzentProJahr,
        data.anzahlVerwendeteJahre,
        trendInfo
    );

    return (
        <div className="trend-detail">
            {/* ============ Klare Bewertungskarte oben ============ */}
            <div className={`bewertung-banner ${bewertung.variant}`}>
                <div className="bewertung-icon">
                    <i className={`bi ${bewertung.icon}`} />
                </div>
                <div className="bewertung-content">
                    <div className="bewertung-headline">{bewertung.headline}</div>
                    <div className="bewertung-text">{bewertung.text}</div>
                </div>
            </div>

            {/* Status-Badge + Anzahl Jahre */}
            <div className="d-flex justify-content-between align-items-center mt-3 mb-2 flex-wrap gap-2">
                <span
                    className="trend-status-badge"
                    style={{ color: trendInfo.color }}
                >
                    <i className={`bi ${trendInfo.icon} me-1`} />
                    {trendInfo.label}
                </span>
                <span className="small trend-info-text">
                    {data.anzahlVerwendeteJahre} Jahre
                    {data.verwendeteJahre && (
                        <span className="ms-1" title={data.verwendeteJahre.join(', ')}>
                            ({data.verwendeteJahre.join(', ')})
                        </span>
                    )}
                </span>
            </div>

            {/* Kennzahlen als Grid */}
            <div className="row g-2 mb-2">
                <div className="col-4">
                    <div className="trend-metric">
                        <div className="trend-metric-label">Aenderung/Jahr</div>
                        <div className="trend-metric-value trend-change-abs">
                            {formatNumber(data.aenderungAbsolutKWhProJahr, 0)}{' '}kWh
                        </div>
                    </div>
                </div>
                <div className="col-4">
                    <div className="trend-metric">
                        <div className="trend-metric-label">Relativ</div>
                        <div className={`trend-metric-value ${getChangeClass(data.aenderungRelativProzentProJahr)}`}>
                            {data.aenderungRelativProzentProJahr >= 0 ? '+' : ''}
                            {formatNumber(data.aenderungRelativProzentProJahr, 2)}%
                        </div>
                    </div>
                </div>
                <div className="col-4">
                    <div className="trend-metric">
                        <div className="trend-metric-label">R&sup2; Qualitaet</div>
                        <div className="trend-metric-value trend-r-squared">
                            {formatNumber(data.rQuadrat, 3)}
                        </div>
                    </div>
                </div>
            </div>

            {/* Begrundung */}
            {data.begrundung && (
                <p className="trend-justification mb-2">{data.begrundung}</p>
            )}

            {/* Disclaimer */}
            {data.fachlicherDisclaimer && (
                <p className="fst-italic small mb-0 trend-disclaimer">
                    <i className="bi bi-info-circle me-1" />
                    {data.fachlicherDisclaimer}
                </p>
            )}
        </div>
    );
}

/**
 * Berechnet die klare Anlagenbewertung.
 * Anlage in Bayern: 48 Grad Neigung, 12 Grad Orientierung (nahe Sud).
 * Typische PV-Degradation: 0,5-0,8 %/Jahr + klimabedingter Hitzeeffekt
 * von ca. 0,5-1,0 % Gesamtverlust durch Temperaturanstieg (+2,3 Grad seit 1951).
 * Steile 48 Grad Neigung -> Kamineffekt als Kuhlung dampft Hitzeeffekt.
 * Mehr-Sonne-Trend in Bayern kann Hitzeverlust teilweise kompensieren.
 */
function computeAnlagenebewertung(prozentProJahr, anzahlJahre, trendInfo) {
    if (trendInfo.label === 'Unzureichende Daten' || trendInfo.label === 'Datenqualitatsproblem') {
        return {
            variant: 'bewertung-warning',
            icon: 'bi-exclamation-triangle',
            headline: 'Keine eindeutige Bewertung moglich',
            text: trendInfo.label === 'Unzureichende Daten'
                ? 'Zu wenige Datenpunkte fur eine Aussage.'
                : 'Datenqualitatsprobleme beeinflussen die Analyse.',
        };
    }

    // Erweiterte Toleranz: Module (0,5-0,8%) + Klimaerwarmungseffekt (~0,2%/Jahr verteilt)
    var normalDegradationMax = -1.2;
    var kritischerSchwellenwert = -1.8;

    if (prozentProJahr >= normalDegradationMax) {
        return {
            variant: 'bewertung-ok',
            icon: 'bi-check-circle-fill',
            headline: 'Anlage im Normalbereich',
            text: prozentProJahr >= 0
                ? 'Der Ertrag ist nicht gesunken. Die Anlage arbeitet wie erwartet — der Mehr-Sonne-Effekt in Bayern kompensiert moglichen Hitzeverlust.'
                : 'Geringer jahrlicher Rueckgang (' + formatNumber(Math.abs(prozentProJahr), 1) + '%/Jahr). Im erwarteten Rahmen: Module alternung (0,5-0,8 %/Jahr) zuzuglich klimabedingter Hitzeeffekte. Die steile 48 Grad Neigung dampft diese Wirkung durch bessere Hinterluftung.',
        };
    }

    if (prozentProJahr >= kritischerSchwellenwert && prozentProJahr < normalDegradationMax) {
        return {
            variant: 'bewertung-aufmerksam',
            icon: 'bi-exclamation-circle-fill',
            headline: 'Rueckgang leicht uber Erwartung',
            text: formatNumber(prozentProJahr, 2) + '%/Jahr liegt etwas uber dem kombinierten Normalbereich (Module + Klima). Untersuchung kann sinnvoll sein — mogliche Ursachen: Verschmutzung, neue Verschattung, Wechselrichter-Alterung. Hitzetage ueber 30 Grad konnen temporar 12-14 % Leistungsverlust pro Tag verursachen.',
        };
    }

    return {
        variant: 'bewertung-kritisch',
        icon: 'bi-x-octagon-fill',
        headline: 'Deutlicher Leistungsverlust',
        text: formatNumber(prozentProJahr, 2) + '%/Jahr liegt deutlich uber der normalen Alterung. Technische Prufung empfehlenswert.',
    };
}

function getChangeClass(value) {
    if (value === null || value === undefined) return '';
    if (value >= -0.5) return 'text-success';
    if (value >= -1.0) return 'text-warning';
    if (value >= -2.0) return 'text-danger-light';
    return 'text-danger';
}

/* ------------------------------------------------------------------ */
/*  Ausgeschlossene-Jahre-Liste                                       */
/* ------------------------------------------------------------------ */

function ExcludedYearsList({ items }) {
    return (
        <ul className="list-group list-group-flush">
            {items.map((item, i) => (
                <li key={i} className="list-group-item px-0 py-1">
                    <strong>{item.jahr}</strong> — {item.grund}
                </li>
            ))}
        </ul>
    );
}

/* ------------------------------------------------------------------ */
/*  Jahreswerte-Tabelle (vollstaendig mit Ausschlussgrund)              */
/* ------------------------------------------------------------------ */

function YearOverviewTable({ items }) {
    return (
        <div className="table-responsive">
            <table
                className="table table-sm table-bordered mb-0 pv-year-table"
                aria-label="Jahreswerte-Tabelle"
            >
                <thead>
                    <tr>
                        <th scope="col">Jahr</th>
                        <th scope="col">Gesamtertrag (kWh)</th>
                        <th scope="col">kWp</th>
                        <th scope="col">Spez. Ertrag</th>
                        <th scope="col">Abw. Vorjahr</th>
                        <th scope="col">Vollst&auml;ndigkeit</th>
                        <th scope="col">Qualit&auml;t</th>
                        <th scope="col">Trend</th>
                        <th scope="col" className="ausschlussgrund-col">Ausschlussgrund</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((item, i) => (
                        <tr
                            key={i}
                            className={getYearRowClass(item)}
                            title={getYearTooltip(item)}
                        >
                            <td>{item.jahr}</td>
                            <td>{formatNumber(item.gesamtertragKWh, 0)}</td>
                            <td>{formatNumber(item.kwp, 1)}</td>
                            <td>{formatNumber(item.spezifischerErtragKWhPerKwp, 0)}</td>
                            <td className={getAbweichungClass(item)}>
                                {item.abweichungRelativProzent !== null && item.abweichungRelativProzent !== undefined
                                    ? (item.abweichungRelativProzent >= 0 ? '+' : '')
                                        + formatNumber(item.abweichungRelativProzent, 1) + '%'
                                    : '—'}
                            </td>
                            <td>{formatNumber(item.vollstaendigkeitProzent, 0)}%</td>
                            <td>
                                <span className="badge" style={{ backgroundColor: qualitaetBadgeColor(item.datenqualitaet) }}>
                                    {QUALITAT_LABEL[item.datenqualitaet] || '?'}
                                </span>
                            </td>
                            <td>
                                {item.inTrendEnthalten ? (
                                    <span className="text-success" aria-label="Im Trend enthalten">
                                        <i className="bi bi-check-circle-fill" aria-hidden="true" />
                                    </span>
                                ) : (
                                    <span className="text-secondary" aria-label="Nicht im Trend">
                                        <i className="bi bi-dash-circle" aria-hidden="true" />
                                    </span>
                                )}
                            </td>
                            <td className="ausschlussgrund-cell">
                                {item.ausschlussgrund || '—'}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function getYearRowClass(item) {
    if (item.voelligkeit === 2) return 'table-info'; // Aktuelles Jahr
    if (!item.inTrendEnthalten) return 'table-secondary'; // Ausgeschlossen
    if (item.vollstaendigkeitProzent < 80) return 'table-warning';
    return '';
}

function getAbweichungClass(item) {
    var abw = item.abweichungRelativProzent;
    if (abw === null || abw === undefined) return '';
    if (abw >= 0) return 'text-success';
    return 'text-danger';
}

function qualitaetBadgeColor(status) {
    switch (status) {
        case 0: return '#198754'; // Gut – green
        case 1: return '#ffc107'; // Eingeschraenkt – yellow
        case 2: return '#dc3545'; // Mangelhaft – red
        default: return '#6c757d'; // Grau
    }
}

function getYearTooltip(item) {
    var parts = [String(item.jahr)];
    if (!item.inTrendEnthalten && item.ausschlussgrund) {
        parts.push('Ausgeschlossen: ' + item.ausschlussgrund);
    }
    parts.push('Qualitaet: ' + (QUALITAT_LABEL[item.datenqualitaet] || '?'));
    parts.push('Vollstaendigkeit: ' + formatNumber(item.vollstaendigkeitProzent, 0) + '%');
    return parts.join(' | ');
}

/* ------------------------------------------------------------------ */
/*  Hilfsfunktionen                                                    */
/* ------------------------------------------------------------------ */

function formatNumber(val, decimals) {
    if (val === null || val === undefined) return '—';
    var num = typeof val === 'string' ? parseFloat(val) : val;
    if (isNaN(num)) return '—';
    return num.toLocaleString('de-DE', {
        minimumFractionDigits: decimals || 0,
        maximumFractionDigits: decimals || 0,
    });
}

function formatDateTime(dateStr) {
    if (!dateStr) return '—';
    try {
        var d = new Date(dateStr);
        return d.toLocaleString('de-DE', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    } catch (_) {
        return dateStr;
    }
}
